import { ApolloQueryResult, useQuery } from '@apollo/client'
import {
  QUERY_TERMS,
  QUERY_SIGNATURE,
  QUERY_MERCHANT_ONBOARDING_STATUS,
  QUERY_MERCHANT_HAS_STRIPE_SUBSCRIPTION
} from 'components/Layout/LayoutQueries'
import { QUERY_MERCHANT_CONNECTED_CARDS } from 'components/Merchants/MerchantQueries'
import moment from 'moment'
import { isAdmin, useSession } from 'packages/@slerp/accounts'
import React, { useContext, useEffect, useState } from 'react'

export interface Term {
  terms_and_conditions: string
  id: string
  inserted_at: Date
  updated_at: Date
}

export interface Signature {
  agreed: boolean
  terms_id: string
}

interface hasStripeSubscription {
  merchantHasStripeSubscription: boolean
}

type OnboardingStatusKeys =
  | 'hasCard'
  | 'hasLogos'
  | 'hasDiscount'
  | 'hasKeyImages'
  | 'hasLoyaltyCard'
  | 'hasModifier'
  | 'hasProduct'
  | 'hasStore'
  | 'hasStripeSetup'
export type OnboardingStatus = Record<OnboardingStatusKeys, boolean>

export interface Card {
  id: string
  last4: number
  expMonth: number
  expYear: number
  expired?: boolean
  defaultSource: string
}

interface GlobalQuery {
  terms: Array<Term> | null
  signatures: Array<Signature> | null
  refetchSignatures:
    | ((
        variables?: Partial<{ merchantId: string }> | undefined
      ) => Promise<ApolloQueryResult<{ signatures: Signature[] }>>)
    | null
  hasStripeSubscription: boolean
  onboardingStatus: OnboardingStatus | null
  cards: Array<Card> | null
  hasExpiredDefaultCard: boolean | null
  isLoading: boolean
  collapsed: boolean
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>
  isChangingOrderStatus: boolean
  setIsChangingOrderStatus: React.Dispatch<React.SetStateAction<boolean>>
}

const defaultValues: GlobalQuery = {
  terms: null,
  signatures: null,
  refetchSignatures: null,
  hasStripeSubscription: false,
  onboardingStatus: null,
  cards: null,
  hasExpiredDefaultCard: null,
  isLoading: false,
  collapsed: false,
  setCollapsed: () => null,
  isChangingOrderStatus: false,
  setIsChangingOrderStatus: () => null
}

export const GlobalQueryContext =
  React.createContext<GlobalQuery>(defaultValues)

export const GlobalQueryProvider = ({
  children
}: {
  children: React.ReactNode
}) => {
  const { merchant, user } = useSession()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isChangingOrderStatus, setIsChangingOrderStatus] =
    useState<boolean>(false)
  const [terms, setTerms] = useState<Array<Term> | null>(null)
  const [signatures, setSignatures] = useState<Array<Signature> | null>(null)

  const [hasStripeSubscription, setHasStripeSubscription] =
    useState<boolean>(false)
  const [onboardingStatus, setOnboardingStatus] =
    useState<OnboardingStatus | null>(null)
  const [cards, setCards] = useState<Array<Card> | null>(null)
  const [hasExpiredDefaultCard, setHasExpiredDefaultCard] = useState<
    boolean | null
  >(null)
  const [collapsed, setCollapsed] = useState<boolean>(false)

  // terms and conditions
  const { loading: isLoadingTerms } = useQuery<{ terms: Array<Term> }>(
    QUERY_TERMS,
    {
      onCompleted: (data) => {
        if (!data) return
        setTerms(data.terms)
      },
      skip: !isAdmin(user)
    }
  )

  const { refetch: refetchSignatures, loading: isLoadingSignatures } =
    useQuery<{ signatures: Array<Signature> }>(QUERY_SIGNATURE, {
      variables: { merchantId: merchant?.id || '' },
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        if (!data) return
        setSignatures(data.signatures)
      },
      skip: !isAdmin(user)
    })

  // stripe subscription checker
  const { loading: isLoadingHasSubscription } = useQuery<hasStripeSubscription>(
    QUERY_MERCHANT_HAS_STRIPE_SUBSCRIPTION,
    {
      variables: { id: merchant?.id },
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        if (!data) return
        setHasStripeSubscription(data.merchantHasStripeSubscription)
      },
      skip: !isAdmin(user)
    }
  )

  // onboarding status
  const { loading: isLoadingOnboardingStatus } = useQuery<{
    merchantOnboardingStatus: OnboardingStatus
  }>(QUERY_MERCHANT_ONBOARDING_STATUS, {
    variables: { id: merchant?.id },
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (!data) return
      setOnboardingStatus(data.merchantOnboardingStatus)
    },
    skip: !isAdmin(user)
  })

  const { loading: isLoadingCards } = useQuery<{ cards: Array<Card> }>(
    QUERY_MERCHANT_CONNECTED_CARDS,
    {
      variables: { merchantSlug: merchant?.slug || '' },
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        if (!data) return
        const cards = data.cards

        const cardsWithExpiry = cards.map((card: Card) => {
          const currentMonth = moment().format('YYYY-MM')
          const expiryMonth = moment(`${card.expYear}-${card.expMonth}`).format(
            'YYYY-MM'
          )

          return {
            ...card,
            expired: moment(currentMonth).isAfter(expiryMonth)
          }
        })

        setHasExpiredDefaultCard(
          cards?.some(
            (card: Card) => card.id === card.defaultSource && card.expired
          )
        )
        setCards(cardsWithExpiry)
      }
    }
  )

  useEffect(() => {
    if (
      isLoadingTerms ||
      isLoadingSignatures ||
      isLoadingHasSubscription ||
      isLoadingOnboardingStatus ||
      isLoadingCards
    ) {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  }, [
    isLoadingTerms,
    isLoadingSignatures,
    isLoadingHasSubscription,
    isLoadingOnboardingStatus,
    isLoadingCards
  ])

  return (
    <GlobalQueryContext.Provider
      value={{
        terms,
        signatures,
        refetchSignatures,
        hasStripeSubscription,
        onboardingStatus,
        cards,
        hasExpiredDefaultCard,
        isLoading,
        collapsed,
        setCollapsed,
        isChangingOrderStatus,
        setIsChangingOrderStatus
      }}
    >
      {children}
    </GlobalQueryContext.Provider>
  )
}

export const useGlobalQuery = () => {
  return useContext(GlobalQueryContext)
}
